import * as React from 'react';
import { DatePicker as MuiDatePicker, DatePickerProps as MuiDatePickerProps } from '@material-ui/pickers';
import { InputAdornment, makeStyles, createStyles } from '@material-ui/core';
import { Event as CalendarIcon } from '@material-ui/icons';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment, { Moment } from 'moment';
import cn from 'classnames';
import TextField from '../TextField/TextField';
import { TranquiTheme } from '../../containers/App/theme';

interface DatePickerProps extends Omit<MuiDatePickerProps, 'onChange' | 'value' | 'shouldDisableDate'> {
  value: Moment | null;
  onChange: (date: Moment | null) => void;
  emptyLabel?: string;
  minDate?: Moment;
  maxDate?: Moment;
  shouldDisableDate?: (date: MaterialUiPickersDate) => boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  onChange,
  className,
  emptyLabel = 'Seleccionar fecha',
  minDate,
  maxDate,
  shouldDisableDate,
  ...props
}) => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();

  const handleChange = (date: MaterialUiPickersDate) => {
    if (date && moment.isMoment(date)) {
      onChange(date);
    } else {
      onChange(null);
    }
  };

  return (
    <MuiDatePicker
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      value={props.value}
      onChange={handleChange}
      disableToolbar
      format={props.format || 'll'}
      emptyLabel={emptyLabel}
      minDate={minDate}
      maxDate={maxDate}
      shouldDisableDate={shouldDisableDate}
      TextFieldComponent={(innerProps) => (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...innerProps}
          variant="outlined"
          InputProps={{
            endAdornment: <InputAdornment position="end" className={classes.icon}><CalendarIcon /></InputAdornment>,
          }}
        />
      )}
      okLabel={false}
      cancelLabel="Cancelar"
      DialogProps={{ className: classes.picker }}
      className={cn(classes.root, className)}
      autoOk
    />
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    '& .MuiInputLabel-outlined': {
      ...theme.typography.body2,
      color: theme.palette.text4.main,
      lineHeight: 'initial',
    },
    '&.Mui-focused': {
      color: theme.palette.black.main,
    },
    '& .MuiOutlinedInput-root': {
      border: 'none',
      borderBottom: 'solid',
      borderColor: `${theme.palette.text5.main}!important`,
      borderWidth: '1px !important',
      borderRadius: '0 !important',
      ...theme.typography.body2,
      color: theme.palette.text2.main,
      fontWeight: 600,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  picker: {
    '& .MuiPickersBasePicker-pickerView': {
      minHeight: 'auto',
    },
    '& .MuiPickersCalendarHeader-switchHeader': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    '& .MuiPickersDay-day': {
      color: theme.palette.text2.main,
    },
    '& .MuiPickersDay-daySelected': {
      backgroundColor: 'transparent',
      border: `solid 1px ${theme.palette.secondary.main}`,
      color: theme.palette.text2.main,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiPickersDay-dayDisabled': {
      color: theme.palette.text5.main,
    },
    '& .MuiPickersDay-current': {
      color: theme.palette.secondary.main,
    },
  },
  icon: {
    '& svg': {
      color: theme.palette.text4.main,
    },
  },
}));

export default DatePicker;
